import { forwardRef, useEffect, useState, useRef } from 'react';
import { formatPriceWithEuro } from '../../../utils/StringUtils';
import {
  getSwileUserinfo,
  paygreenCapture,
  paygreenCreateBuyer,
  paygreenCreateOrder,
} from '../../../actions/userConecsActions';
import useAxios
 from '../../../axios/useAxios';
import { notifyError, notifyInfo } from '../../../utils/Notify';
import { successPaidOrder } from '../../../actions/cartActions';
import { addUsedPromo } from '../../../actions/userActions';
import { useDispatch } from 'react-redux';
import { useOrderLogger } from '../../../hooks/useOrderLogger';
import { PaymentProvider } from '../../paymentConstants.js';
import { calculateTotalToPay } from '../../../utils/order/orderUtils';

const PaygreenSwile = (
  {
    user,
    onCheckboxChange,
    errorMessage,
    order,
    paymentMethod,
    usedPaymentMethod,
    store,
  },
) => {
  // Environment variables
  const paygreen_endpoint = process.env.REACT_APP_PAYGREEN_API_ENDPOINT;
  const paygreen = process.env.REACT_APP_PAYGREEN_PUBLIC_KEY;

  // State management
  const [loadingSwileCreateOrder, setLoadingSwileCreateOrder] = useState(false);
  const [loadingSwilePayment, setLoadingSwilePayment] = useState(false);
  const [userName,] = useState(user?.swile?.name || '');
  const [userEmail,] = useState(user?.swile?.email || '');

  // Refs for component lifecycle management
  const isComponentMounted = useRef(true);
  const paygreenInitialized = useRef(false);
  const paymentProcessing = useRef(false);

  // Hooks
  const axiosInstance = useAxios();
  const dispatch = useDispatch();

  // Computed values
  const isChecked = paymentMethod === usedPaymentMethod;
  const isContentVisible = isChecked;

  const {
    logPaymentInitiated,
    logPaymentFailedWithCard,
  } = useOrderLogger();

  /**
   * Clean up PayGreen instance properly
   */
  const cleanupPaygreen = async () => {
    try {
      if (window.paygreenjs && paygreenInitialized.current) {
        console.log('Cleaning up PayGreen...');

        // Use modern method if available, fallback to deprecated one
        if (window.paygreenjs.off) {
          // Remove specific event listeners
          window.paygreenjs.off(window.paygreenjs.Events.PAYMENT_FLOW_ONCHANGE);
          window.paygreenjs.off(window.paygreenjs.Events.FULL_PAYMENT_DONE);
          window.paygreenjs.off(window.paygreenjs.Events.PAYMENT_FAIL);
          if (window.paygreenjs.Events.ERROR) {
            window.paygreenjs.off(window.paygreenjs.Events.ERROR);
          }
          if (window.paygreenjs.Events.PAYMENT_CANCEL) {
            window.paygreenjs.off(window.paygreenjs.Events.PAYMENT_CANCEL);
          }
        } else if (window.paygreenjs.removeAllEventListeners) {
          // Fallback to deprecated method
          window.paygreenjs.removeAllEventListeners();
        }

        // Wait before unmounting to ensure event listeners are removed
        await new Promise(resolve => setTimeout(resolve, 100));

        // Unmount the widget
        await window.paygreenjs.unmount(true);
        paygreenInitialized.current = false;

        console.log('PayGreen cleanup completed');
      }
    } catch (error) {
      console.warn('Error during PayGreen cleanup:', error);
    }
  };

  /**
   * Reset component states safely
   */
  const resetStates = () => {
    if (isComponentMounted.current) {
      setLoadingSwilePayment(false);
      setLoadingSwileCreateOrder(false);
    }
    paymentProcessing.current = false;
  };

  /**
   * Handle component cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isComponentMounted.current = false;
      cleanupPaygreen();
    };
  }, []);

  /**
   * Initialize PayGreen payment flow
   */
  const initializePaygreen = async (conecsOrder, createBuyerResponse, paygreenShopId) => {
    return new Promise((resolve, reject) => {
      // Remove existing scripts
      const existingScripts = document.querySelectorAll('script[src*="paygreen"]');
      existingScripts.forEach(script => script.remove());

      // Clear PayGreen from window
      window.paygreenjs = undefined;

      console.info('Loading PayGreen script...');
      const script = document.createElement('script');
      script.src = paygreen_endpoint;
      script.async = true;

      script.onload = async () => {
        if (!isComponentMounted.current) {
          console.info('Component unmounted, skipping PayGreen initialization');
          resolve();
          return;
        }

        console.info('PayGreen script loaded, initializing...');
        try {
          await initPaygreenWidget(conecsOrder, createBuyerResponse, paygreenShopId);
          resolve();
        } catch (error) {
          console.error('Widget initialization failed:', error);
          // Reset states on widget initialization failure
          if (isComponentMounted.current) {
            resetStates();
          }
          reject(error);
        }
      };

      script.onerror = () => {
        console.error('Failed to load PayGreen script');
        // Reset states on script loading failure
        if (isComponentMounted.current) {
          resetStates();
        }
        reject(new Error('Script loading failed'));
      };

      document.body.appendChild(script);
    });
  };

  /**
   * Initialize PayGreen widget and attach event listeners
   */
  const initPaygreenWidget = async (conecsOrder, createBuyerResponse) => {
    if (!isComponentMounted.current) {
      console.info('Component unmounted during widget initialization');
      return;
    }

    try {
      await window.paygreenjs.init({
                                     publicKey: paygreen,
                                     mode: 'payment',
                                     buyer: user?.swile?.buyerId || createBuyerResponse?.data?.data?.id,
                                     paymentMethod: 'swile',
                                     paymentOrderID: conecsOrder.id,
                                     objectSecret: conecsOrder.object_secret,
                                     modeOptions: {},
                                   });

      paygreenInitialized.current = true;

      // Attach event listeners
      attachPaygreenEventListeners(conecsOrder, createBuyerResponse);

      console.info('PayGreen initialized successfully');

    } catch (initError) {
      console.error('PayGreen initialization failed:', initError);

      // Reset states on initialization error
      if (isComponentMounted.current) {
        resetStates();
        notifyError('Erreur d\'initialisation du formulaire de paiement');
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: "PAYMENT_FAILED",
          errorMessage: initError.message || "PAYGREEN INIT ERROR",
          paymentMethod: PaymentProvider.SWILE,
          paymentProvider: PaymentProvider.PAYGREEN,
          amountAttempted: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
          currency: 'EUR',
        });
      }

      throw initError;
    }
  };

  /**
   * Attach PayGreen event listeners
   */
  const attachPaygreenEventListeners = (conecsOrder, createBuyerResponse) => {
    // Payment flow change listener
    window.paygreenjs.on(
      window.paygreenjs.Events.PAYMENT_FLOW_ONCHANGE,
      (event) => {
        console.info('Payment flow change:', event);
        if (!isComponentMounted.current) return;
        if (event.detail?.status === 'failed') {
          setLoadingSwilePayment(false);
        }
      },
    );

    // Payment success listener
    window.paygreenjs.on(
      window.paygreenjs.Events.FULL_PAYMENT_DONE,
      async (event) => {
        console.info('Payment successful:', event);

        // Prevent duplicate processing
        if (!isComponentMounted.current || !paymentProcessing.current) {
          console.info('Payment success ignored - component unmounted or not processing');
          return;
        }

        if (event.type === 'FULL_PAYMENT_DONE') {
          try {
            const userSwile = {
              buyerId: user?.swile?.buyerId || createBuyerResponse?.data?.data?.id,
              email: userEmail,
              firstName: userName.split(' ').slice(0, -1).join(' '),
              lastName: userName.split(' ').slice(-1).join(' '),
            };

            // Capture payment
            const captureResponse = await paygreenCapture(
              axiosInstance,
              conecsOrder.id,
              order._id,
              order.storeId,
              user,
              'swile',
            );

            // Dispatch success actions
            dispatch(successPaidOrder(captureResponse.data));
            dispatch(addUsedPromo(user, axiosInstance, order));
            dispatch(getSwileUserinfo(userSwile, user, axiosInstance));

            // Clean up PayGreen
            await cleanupPaygreen();

          } catch (error) {
            console.error('Error processing successful payment:', error);
            if (isComponentMounted.current) {
              notifyError('Erreur lors du traitement du paiement');
              logPaymentFailedWithCard(order?._id, order?.userId,{
                errorCode: "PAYMENT_FAILED",
                errorMessage: "PAYGREEN PAYMENT FAIL",
                paymentMethod: PaymentProvider.SWILE,
                paymentProvider: PaymentProvider.PAYGREEN,
                amountAttempted: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
                currency: 'EUR',
              });
            }
          } finally {
            resetStates();
          }
        }
      },
    );

    // Payment failure listener
    window.paygreenjs.on(
      window.paygreenjs.Events.PAYMENT_FAIL,
      (event) => {
        console.log('Payment failed:', event);
        console.log('Payment failed detail:', event.detail);
        console.log('Payment failed error:', event.error);

        // Check component state first
        if (!isComponentMounted.current) {
          console.log('Payment fail ignored - component unmounted');
          return;
        }

        // Log additional debugging info
        if (event.detail) {
          console.log('Failure reason:', event.detail.reason || 'Unknown');
          console.log('Failure message:', event.detail.message || 'No message');
        }

        // Reset states and notify user
        resetStates();

        // More specific error message based on the failure
        let errorMessage = 'Paiement échoué';
        if (event.detail?.message) {
          errorMessage += ': ' + event.detail.message;
        } else if (event.detail?.reason) {
          errorMessage += ': ' + event.detail.reason;
        }

        notifyError(errorMessage);
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: "PAYMENT_FAILED",
          errorMessage: errorMessage || "PAYGREEN PAYMENT FAIL",
          paymentMethod: PaymentProvider.SWILE,
          paymentProvider: PaymentProvider.PAYGREEN,
          amountAttempted: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
          currency: 'EUR',
        });
      },
    );

    window.paygreenjs.on(
      window.paygreenjs.Events.ERROR,
      (event) => {
        console.log('PayGreen general error:', event);
        if (!isComponentMounted.current) return;

        resetStates();
        notifyError('Erreur technique du système de paiement');
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: "PAYMENT_FAILED",
          errorMessage: errorMessage || "PAYGREEN PAYMENT FAIL",
          failureReason: "SWILE PAYMENT FAIL",
          paymentMethod: PaymentProvider.SWILE,
          paymentProvider: PaymentProvider.PAYGREEN,
          amountAttempted: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
          currency: 'EUR',
        });
      },
    );

    if (window.paygreenjs.Events.PAYMENT_CANCEL) {
      window.paygreenjs.on(
        window.paygreenjs.Events.PAYMENT_CANCEL,
        (event) => {
          console.log('Payment cancelled:', event);
          if (!isComponentMounted.current) return;

          resetStates();
          const errorMessage = "Paiement annulé";
          notifyInfo(errorMessage);
          logPaymentFailedWithCard(   order?._id, order?.userId,{
            errorCode: "PAYMENT_CANCELLED",
            errorMessage: errorMessage || "PAYGREEN PAYMENT CANCELLED",
            paymentMethod: PaymentProvider.SWILE,
            paymentProvider: PaymentProvider.PAYGREEN,
            amountAttempted: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
            currency: 'EUR',
          });
        },
      );
    }
  };

  /**
   * Main payment function
   */
  const swilePayment = async () => {
    // Prevent multiple simultaneous payments
    if (paymentProcessing.current) {
      notifyInfo('Un paiement est déjà en cours');
      return;
    }

    // Set initial states
    setLoadingSwileCreateOrder(true);
    paymentProcessing.current = true;

    try {
      let name;
      let email;

      // Validate user input for new buyers
      if (!user?.swile?.buyerId) {
        name = 'Jean Dupont';
        email = user?.email || 'contact@pikkopay.fr';
      }

      const userInfo = { name, email };

      // Get store PayGreen configuration
      const paygreenShopId = await axiosInstance.get(`store/conecsId/${order.storeId}`);

      // Create buyer if needed
      let createBuyerResponse;
      if (!user?.swile?.buyerId) {
        createBuyerResponse = await paygreenCreateBuyer(userInfo, axiosInstance);
        console.log('Buyer created:', createBuyerResponse);
      }

      // Create PayGreen order
      const response = await paygreenCreateOrder(
        user?.swile?.buyerId ? user?.swile : createBuyerResponse?.data.data,
        calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
        paygreenShopId.data.paygreenShop,
        axiosInstance,
      );

      const conecsOrder = response.data.data;
      console.log('PayGreen order created:', conecsOrder);


      await cleanupPaygreen();

      // Initialize PayGreen
      await initializePaygreen(conecsOrder, createBuyerResponse, paygreenShopId);
      logPaymentInitiated(order?._id, order?.userId, PaymentProvider.SWILE, PaymentProvider.PAYGREEN);

    } catch (error) {
      console.error('Error in swilePayment:', error);

      if (isComponentMounted.current) {
        notifyError('Erreur lors de la création du paiement');
        resetStates();
        logPaymentFailedWithCard(order?._id, order?.userId,{
          errorCode: "PAYMENT_FAILED",
          errorMessage: errorMessage || "SWILE PAYMENT ERROR",
          paymentMethod: PaymentProvider.SWILE,
          paymentProvider: PaymentProvider.PAYGREEN,
          amountAttempted: calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount),
          currency: 'EUR',
        });
      }
    }
  };

  return (
    <>
      <button
        type='button'
        className={`flex items-center formcarte text-black px-8 pr-14 w-full ${
          isContentVisible ? 'slide-down-btn open' : ''
        }`}
        onClick={onCheckboxChange}
        disabled={!store?.TR_CONECS}
      >
        <div className="flex w-[70%] items-center">
          {store?.TR_CONECS && (
            <>
              <input
                type="checkbox"
                onChange={onCheckboxChange}
                className="paygreencheckbox"
                id="paygreenCheckbox"
                checked={isChecked}
              />
              <label
                htmlFor="paygreenCheckbox"
                className="paygreenlabel mr-4"
              />
            </>
          )}
          <div
            className="text-[16px] text-start"
            style={{ fontFamily: 'Open Sans' }}
          >
            {store?.TR_CONECS ? 'Swile' : 'Bientôt disponible'}
          </div>
          <div className="paygreenimg h-full" />
        </div>
        <div className="cbimg flex gap-2 no-wrap">
          <img
            className="h-10 rounded-[5px]"
            src="https://storage.googleapis.com/pikkopay_images/webapp/payment/swile.png"
            alt="Swile"
          />
        </div>
      </button>

      <div id="paygreen-container" style={{ display: 'none' }} />

      {isContentVisible && (
        <div
          id="paygreen-conecs"
          style={{ fontFamily: 'Open Sans' }}
          className="slide-down-transition slide-down-content visible grey-bg"
        >
          <div className="payment-error full no-bottom">{errorMessage}</div>

            <div className="flex flex-col w-full">
              <button
                type='button'
                className={`payer_maintenant_btn flex items-center bottom-btn-shadow ${
                  loadingSwilePayment ? 'pointer-events-none backgray2 opacity-35' : ''
                }`}
                onClick={swilePayment}
                disabled={loadingSwileCreateOrder || loadingSwilePayment}
              >
                {loadingSwileCreateOrder || loadingSwilePayment ? (
                  <p>Paiement en cours...</p>
                ) : (
                  <>
                    <p
                      className="font-light"
                      style={{ fontFamily: 'geomanistmedium' }}
                    >
                      Payer maintenant
                    </p>
                    <p>{formatPriceWithEuro(calculateTotalToPay(order.itemsPrice, order.promoPrice, order?.jackpot?.amount))}</p>
                  </>
                )}
              </button>
            </div>
          {/*)}*/}

        </div>
      )}
    </>
  );
};

export default forwardRef(PaygreenSwile);
